// hooks/useCases.ts — reactive hooks for the moderation cases domain.
//
// Components must use these hooks, not caseStore or casesService directly.

import { useShallow } from 'zustand/react/shallow';
import { useCaseStore } from '@/core/stores/caseStore';
import { casesService } from '@/core/services/casesService';
import type { CaseRecord, ModerationCategory, VoteSide } from '@/core/models/case';

export interface CasesHook {
  /** All cases in reverse chronological order (most-recent first). */
  cases: CaseRecord[];
  /** Return a single case by id. */
  getCase: (id: string) => Promise<CaseRecord | null>;
  /** Create a post-kind case from a report. */
  createCaseFromReport: (postId: string, category: ModerationCategory, context?: string) => Promise<CaseRecord>;
  /** Create an account-kind case from a report. */
  createAccountCaseFromReport: (accountId: string, category: ModerationCategory, context?: string) => Promise<CaseRecord>;
  /** Cast a keep/remove vote on a case. */
  castVote: (caseId: string, side: VoteSide) => Promise<CaseRecord | null>;
  /** Appeal a decided case. `reason` is required by the server (see casesService.appeal). */
  appeal: (caseId: string, reason: string) => Promise<CaseRecord | null>;
}

/**
 * useCases — full cases list + mutation API.
 * Subscribes to the entire cases array (re-renders on every case change).
 */
export function useCases(): CasesHook {
  const cases = useCaseStore(useShallow((s) => [...s.cases].reverse()));

  return {
    cases,
    getCase: (id) => casesService.getCase(id),
    createCaseFromReport: (postId, category, context) =>
      casesService.createCaseFromReport({ kind: 'post', targetId: postId, category, context }),
    createAccountCaseFromReport: (accountId, category, context) =>
      casesService.createAccountCaseFromReport({ targetId: accountId, category, context }),
    castVote: (caseId, side) => casesService.castVote(caseId, side),
    appeal: (caseId, reason) => casesService.appeal(caseId, reason),
  };
}

/**
 * usePostStatus — returns the most-recent case for a given postId, or null.
 * Uses a plain (non-shallow) selector because it returns a single value (or null).
 * Re-renders when any case mutation changes the array.
 */
export function usePostStatus(postId: string): CaseRecord | null {
  return useCaseStore((s) => {
    for (let i = s.cases.length - 1; i >= 0; i--) {
      const c = s.cases[i]!;
      if (c.kind === 'post' && c.postId === postId) return c;
    }
    return null;
  });
}

/**
 * useAccountStatus — returns the most-recent case for a given accountId (handle), or null.
 */
export function useAccountStatus(accountId: string): CaseRecord | null {
  return useCaseStore((s) => {
    for (let i = s.cases.length - 1; i >= 0; i--) {
      const c = s.cases[i]!;
      if (c.kind === 'account' && c.accountId === accountId) return c;
    }
    return null;
  });
}
